// Pure helpers for interrupted-recording retention and recovery.
// No IndexedDB dependency, so they stay unit-testable under `node --test`
// (see interrupted_recording_helpers.test.mjs).

const RETENTION_MS = 7 * 24 * 60 * 60 * 1000 // 7 days — matches iOS storage eviction
const MAX_BYTES = 200 * 1024 * 1024 // ~200MB cap, evict oldest

// After stop+transcribe is interrupted (common on iOS lock), auto-resume is only
// attempted while this window is open — older pending sessions fall back to the
// "Interrupted recording found" toast so we don't surprise-submit hours later.
export const PENDING_RESUME_MAX_AGE_MS = 2 * 60 * 60 * 1000 // 2 hours

// The key every recording is persisted under when its surface provides no
// restoreKey. Such recordings are still saved and recoverable — just from the
// global Interrupted recordings list rather than via an in-place restore toast
// (which needs a stable per-surface anchor).
export const UNKEYED_RECORDING_KEY = 'voice:unkeyed'

// A recording must never be lost just because its surface didn't set a restoreKey,
// so persistence always runs under *some* key: the surface's own, or the shared
// unkeyed fallback.
export function persistenceKey(restoreKey) {
  return restoreKey || UNKEYED_RECORDING_KEY
}

// True when a stored session should silently re-transcribe (and optionally
// auto-submit) on reconnect — e.g. user locked the phone mid-transcription.
export function isPendingResumeEligible(session, now = Date.now()) {
  if (!session?.pendingResume) return false
  const at = session.pendingResumeAt ?? session.startedAt
  if (at == null) return false
  return now - at <= PENDING_RESUME_MAX_AGE_MS
}

// True when an ajax:success JSON body proves the write landed on the server.
//
// Rails remote forms often return HTTP 200 with `{ alert: "…" }` for soft
// failures (blank message, guest limit, etc.). Treating those as durable saves
// would call commitConsumed() and permanently delete the IndexedDB recovery
// copy — exactly how a "submit timed out / session expired" path can lose a
// recording. Only drop local recovery copies when the response also includes a
// save signal (message append, form reset, replace, redirect, …).
export function serverAcknowledgedDurableSave(data) {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) {
    return false
  }

  // AjaxHelper accepts reset_form as true (reset the submitting form) or a CSS
  // selector string that targets a specific form — both mean the write landed.
  if (data.reset_form === true) return true
  if (typeof data.reset_form === 'string' && data.reset_form.length > 0)
    return true
  if (nonEmptyObject(data.append)) return true
  if (nonEmptyObject(data.replace)) return true
  if (nonEmptyObject(data.prepend)) return true
  if (nonEmptyObject(data.update)) return true
  if (typeof data.content === 'string' && data.content.length > 0) return true
  if (typeof data.redirect === 'string' && data.redirect.length > 0) return true

  return false
}

function nonEmptyObject(value) {
  return (
    value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).length > 0
  )
}

// Network / transient failures from fetch or our transcription endpoint. Real
// "bad audio" / auth problems are not recoverable by retrying the same blob.
export function isRecoverableTranscriptionError(error) {
  if (!error) return false
  if (error.recoverable === true) return true
  // Explicit non-recoverable tagging from the HTTP layer (transcribe.js sets
  // recoverable = false for 400/401/403/404 etc.) must win over message
  // heuristics — the default message is "Transcription failed (status)" which
  // would otherwise match and silently retry client errors.
  if (error.recoverable === false) return false

  const name = error.name || ''
  if (
    name === 'TypeError' ||
    name === 'AbortError' ||
    name === 'NetworkError'
  ) {
    return true
  }

  const status = error.status
  if (typeof status === 'number') {
    if (status === 408 || status === 429) return true
    if (status >= 500 && status <= 599) return true
    // Our controller maps OpenAI/upstream failures to 422 — retry a few times.
    if (status === 422) return true
  }

  const message = String(error.message || '').toLowerCase()
  return (
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('load failed') ||
    message.includes('the internet connection appears to be offline') ||
    message.includes('transcription failed')
  )
}

export function expiredSessions(records, now, retentionMs = RETENTION_MS) {
  return records.filter((r) => now - r.startedAt > retentionMs)
}

// Oldest-first eviction until total bytes fit under the cap.
export function sessionsOverCap(records, maxBytes = MAX_BYTES) {
  const total = records.reduce((sum, r) => sum + r.bytes, 0)
  if (total <= maxBytes) return []

  const oldestFirst = [...records].sort((a, b) => a.startedAt - b.startedAt)
  const evict = []
  let remaining = total
  for (const record of oldestFirst) {
    if (remaining <= maxBytes) break
    evict.push(record)
    remaining -= record.bytes
  }
  return evict
}

// Human duration: '32s', '4m 12s'.
export function formatDurationLong(ms) {
  const total = Math.round(ms / 1000)
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return minutes ? `${minutes}m ${seconds}s` : `${seconds}s`
}
